import videojs from 'video.js'
import BasePlugin from './base-plugin'
import { setIcon, vjs_El, vjs_find } from './utils'

const touchActiveClass = 'vjs-touch-active'
const touchInactiveClass = 'vjs-touch-inactive'

function iconBtnHtml(id, label) {
  return `<span class="vjs-icon-placeholder" aria-hidden="true" data-id="${id}"></span><span class="vjs-control-text" aria-live="polite">${label}</span>`
}

function getTouchX(e) {
  return e.touches[0].pageX || null
}

videojs.options.controlBar = {
  children: [
    'playToggle',
    'liveDisplay',
    'seekToLive',
    'progressControl',
    'currentTimeDisplay',
    'timeDivider',
    'durationDisplay',
    'customControlSpacer',
    'volumePanel',
    'chaptersButton',
    'descriptionsButton',
    'subsCapsButton',
    'audioTrackButton',
    'pictureInPictureToggle',
    'fullscreenToggle',
  ],
}

class DubbUiPlugin extends BasePlugin {
  constructor(player) {
    super(player)
    this.options = {
      dvrMin: 300,
      minhd: 1080,
    }

    this.disposed = false
    this.firstPlay = true
    this.touchtimer = null
    this.inactivityTimeout = this.player.options_.inactivityTimeout
    this.inactivityTouchTimeout = 4e3
    if (this.player.options_.inactivityTouchTimeout) {
      this.inactivityTouchTimeout = this.player.options_.inactivityTouchTimeout
    }
    this.bigPlayButtonEl = this.playerElement.querySelector('.vjs-big-play-button')
    this.controlBarEl = vjs_find(this.playerElement, '.vjs-control-bar')
    this.initDeviceSpecificOptions()
    this.initBasicControls()
    this.prepareSvgIcons()
    this.configPlayerBehavior()
    this.initTextTrackStyle()
    this.initProgressBar()
    this.initPlayerActiveManagement()
    this.replaceSvgIcons()
    this.playerElement.style.visibility = 'visible'
    if (player.options_.sources.length > 0) {
      player.trigger('sourcesready', player.options_.sources)
    }
    player.on('reload', (e, sources) => {
      player.trigger('sourcesready', sources)
    })
  }

  initDeviceSpecificOptions = () => {
    if (videojs.browser.IS_ANDROID) {
      videojs.options.html5.nativeAudioTracks = false
      videojs.options.html5.nativeTextTracks = false
      try {
        videojs.options.vhs.overrideNative = true
      }
      catch (_err) {
      }
    }
    if (videojs.browser.IS_IPAD && 'MediaSource' in window) {
      videojs.options.html5.nativeTextTracks = false
    }
    if ('MediaSource' in window) {
      try {
        videojs.options.vhs.overrideNative = true
      }
      catch (_err) {
      }
    }
  }

  initBasicControls = () => {
    if (!this.tech.autoplay) {
      this.bigPlayButtonEl.classList.add('vjs-bp-block')
    }
    this.playerElement.insertBefore(this.dom.createEl('div', { className: 'vjs-bg-bar' }), this.controlBarEl)
    const customControlSpacerEl = vjs_find(this.playerElement, '.vjs-custom-control-spacer')
    if (customControlSpacerEl) {
      customControlSpacerEl.innerHTML = ''
    }

    const posterEl = vjs_find(this.playerElement, '.vjs-poster')
    const touchNavEl = vjs_El('div', 'vjs-touch-nav')
    this.playerElement.insertBefore(touchNavEl, this.bigPlayButtonEl)
    const playBtnComp = this.player.addChild('button', {
      el: this.dom.createEl(
        'button',
        { className: 'vjs-big-button vjs-b-p-b' },
        {
          'type': 'button',
          'aria-label': 'Play',
          'aria-disabled': 'false',
          'tabindex': '0',
        },
      ),
    })
    this.playerElement.insertBefore(touchNavEl, this.bigPlayButtonEl)
    playBtnComp.el().innerHTML = iconBtnHtml('play', 'Play')
    touchNavEl.appendChild(playBtnComp.el())
    playBtnComp.el().ontouchend = (e) => {
      e.stopImmediatePropagation()
      this.triggerInactiveTimer()
      if (this.player.paused()) {
        this.player.play()
      }
      else {
        this.player.pause()
      }
    }
    this.bigPlayButtonEl.ontouchend = posterEl.ontouchend = (e) => {
      e.stopImmediatePropagation()
      if (this.firstPlay) {
        this.dom.addClass(this.playerElement, touchInactiveClass)
      }
    }
    this.controlBarEl.addEventListener('touchstart', this.triggerInactiveTimer, { passive: true })
    this.controlBarEl.addEventListener('touchmove', this.triggerInactiveTimer, { passive: true })
    this.player.on('dispose', () => {
      this.controlBarEl.removeEventListener('touchstart', this.triggerInactiveTimer)
      this.controlBarEl.removeEventListener('touchmove', this.triggerInactiveTimer)
    })

    const replayBtnComp = this.player.addChild('button', {
      el: this.dom.createEl(
        'button',
        { className: 'vjs-replay-button' },
        { 'type': 'button', 'title': 'Replay', 'aria-disabled': 'false' },
      ),
    })
    this.playerElement.insertBefore(replayBtnComp.el_, this.bigPlayButtonEl)
    replayBtnComp.el_.innerHTML = iconBtnHtml('big-replay', 'Replay')
    replayBtnComp.el_.onclick = replayBtnComp.el_.ontouchend = (e) => {
      e.preventDefault()
      e.stopImmediatePropagation()
      this.player.currentTime(0)
      this.player.play()
      this.player.trigger('replay')
    }
    this.player.on('ended', () => {
      this.dom.removeClass(replayBtnComp.el_, this.hiddenClass)
    })
  }

  prepareSvgIcons = () => {
    const svgContent = `<svg xmlns="http://www.w3.org/2000/svg"><defs>
<symbol id="vjs-icon-pip-full" viewBox="0 0 512 512"><path d="M407 77H108q-20 0-35 12t-15 31v259q0 18 15 31t35 12h299q21 0 34-12t13-31V120q0-18-13-31t-34-12zm15 307H97l-1-270h326v270zM117 133h279v230H117V133z"></path></symbol>
<symbol id="vjs-icon-expand" viewBox="0 0 446 512"><path d="M50 409h41V90H50zM355 90v319h41V90zm-81 183h-42l-1-28 43-1v-60l82 75-82 75zm-99 2 43-1v-28l-43 1v-61l-85 74 85 74z"></path></symbol>
<symbol id="vjs-icon-expand-back" viewBox="0 0 446 512"><path d="M142 409h45V90h-45zM260 90v319h45V90zm127 163h51v-29l-51 1v-68l-84 82 84 83zm-328-1H8l1-29 50-1v-69l84 83-84 82z"></path></symbol>
<symbol id="vjs-icon-center" viewBox="0 0 512 512"><path d="M448.3 225.3h-38q-4-25-15.6-47-11-22-28.2-39.3t-39.3-28.3q-22-11.5-47-15.5v-38h-48.4v38q-25 4-47 15.5-22 11-39.3 28.3t-28.3 39.3q-11.5 22-15.5 47h-38v48.4h38q4 25 15.5 47 11 22 28.3 39.3t39.3 28.3q22 11.4 47 15.4v38h48.4v-38q25-4 47-15.4 22-11 39.3-28.3t28.3-39.3q11.4-22 15.4-47h38v-48.4zm-87 0h-37.6q-5-15-16.7-26.8t-26.8-16.8v-37.4q30 6.4 52.3 28.7t28.8 52.3zm-105 48.4q-10 0-17.3-7.2t-7.3-17.3q0-9.4 7.3-16.7t16.8-7.3q10 0 17.2 7.3t7.3 16.8q0 10-7.3 17.2t-16.8 7.3zm-24.6-129.4v37.4q-15 5-26.7 16.8t-16.8 26.8h-37.4q6.4-30 28.7-52.3t52.3-28.8zm-81 129.4h37.6q5 15 16.7 26.8t26.8 16.8v37.4q-30-6.4-52.3-28.7t-28.8-52.3zm129.6 81v-37.4q15-5 26.7-16.8t16.8-26.8h37.4q-6.4 30-28.7 52.3t-52.3 28.8z"></path></symbol>
<symbol id="vjs-icon-big-replay" viewBox="0 0 512 512"><path d="m326.8 135.2 32.6-71.4L407.6 194l-119.2 22 29.3-64c-14.8-6.8-32-10.7-47.4-11-15.6-.2-39.3 2.4-53.4 7.5s-38.9 21.2-50.3 31.6A145.4 145.4 0 0 0 136 224a149.3 149.3 0 0 0-11.5 52.6c-.4 15.4 2.3 29.6 7.7 43.7s16.6 36 26.9 47.7c10.4 11.4 27.4 27.3 42.3 34s43 11.6 58.5 12c15.6.2 40.3-6 54.4-11.2s29.7-15.8 41-26.2a135 135 0 0 0 30.2-41.4l18.6 8.5c-9 19.5-21 36.3-36.4 50.2s-32.5 24.4-51.3 31.5c-18.9 7-38.6 10.2-59.4 9.8-20.8-.3-41.2-4.9-61-14s-36.6-21.3-50.4-37a157.7 157.7 0 0 1-41.3-110.6c.5-20.7 5.2-40.9 14.1-60.4a158 158 0 0 1 208.3-78z"></path></symbol>
<symbol id="vjs-icon-cancel" viewBox="0 0 409 512"><path d="M377 354q0 12-8 20l-40 40q-8 8-20 8-11 0-19-8l-85-86-85 86q-8 8-20 8t-20-8l-39-40q-8-8-8-20 0-11 8-19l85-85-85-85q-8-8-8-20t8-20l39-39q8-8 20-8t20 8l85 85 85-85q8-8 19-8 12 0 20 8l40 39q8 8 8 20t-8 20l-86 85 86 85q8 8 8 19z"></path></symbol>
<symbol id="vjs-icon-fullscreen" viewBox="0 0 445 512"><path d="M219 291q0 3-3 6l-86 87 37 37q5 5 5 12t-5 12-11 5H39q-7 0-12-5t-5-12V316q0-7 5-12t12-5 11 5l38 38 87-86q2-3 5-3 4 0 6 3l30 29q3 3 3 6zM423 66v117q0 7-5 12t-12 5-12-5l-37-38-87 86q-2 3-6 3-3 0-6-3l-30-29-2-6 2-6 87-87-38-37q-5-5-5-12t5-12 12-5h117q7 0 12 5t5 12z"></path></symbol>
<symbol id="vjs-icon-fullscreen-back" viewBox="0 0 445 512"><path d="M222 266v117q0 7-5 12t-11 4-12-4l-38-38-86 87q-3 2-7 2-3 0-5-2l-30-30-2-6q0-4 2-6l86-86-37-38q-5-5-5-12t5-12q5-4 12-4h117q6 0 11 4 5 5 5 12zM419 91q0 4-3 6l-86 86 37 38q5 5 5 12t-5 12-12 5H239q-7 0-12-5t-5-12V116q0-7 5-12t12-4 12 4l37 38 86-87q3-2 7-2 3 0 5 2l30 30q3 3 3 6z"></path></symbol>
<symbol id="vjs-icon-play" viewBox="0 0 512 512"><path d="M137 98v303l238-151z"></path></symbol>
<symbol id="vjs-icon-pause" viewBox="0 0 512 512"><path d="M127 401h86V98h-86v303zM299 98v303h87V98h-87z"></path></symbol>
<symbol id="vjs-icon-mute" viewBox="0 0 512 512"><path d="M353 256.7q0-28.5-15-51.8t-39-35.2v47.5l53.5 53q.5-3 .5-6.5v-7zm54.5 0q0 15-3.3 29.2t-8.7 27.8l33 32.5q10.5-20 16.3-42.8t5.7-46.7q0-35-11.5-66t-31.8-56-47.7-42.5q-28-17.5-60.5-25v44.5q23.5 7 43.5 21 20 13.5 34.3 32.5t22.2 42.5q8.5 23 8.5 49zM89 62.2 61.5 89.7l102 102h-102v129.5H148l108 108v-145l92 92q-11 8-23.3 14.7T299 401.7v44.5q22.5-5 42.8-15t37.2-24l44 44 27.5-27.5L89 62.2zm167 21.5-45 45 45 45.5V83.7z"></path></symbol>
<symbol id="vjs-icon-volume3" viewBox="0 0 512 512"><path d="M61.5 191.7v129.5H148l108 108V83.7l-108 108H61.5zm291.5 65q0-28.5-15-51.8t-39-35.2v174q24-12 39-35.3t15-51.7zM299 67.2v44.5q23.5 7 43.5 21 20 13.5 34.3 32.5t22.2 42.5q8.5 23 8.5 49 0 25.5-8.5 49-8 23-22.3 42t-34.2 33-43.5 20.5v45q32.5-7.5 60.5-25 27.5-17.5 47.8-42.5t31.7-56q11.5-31.5 11.5-66 0-35-11.5-66t-31.8-56-47.7-42.5q-28-18-60.5-25z"></path></symbol>
<symbol id="vjs-icon-pip" viewBox="0 0 512 512"><path d="M407 77H108q-20 0-35 12t-15 31v259q0 18 15 31t35 12h299q21 0 34-12t13-31V120q0-18-13-31t-34-12zm15 307H97l-1-270h326v270zM264 254h133v109H264V254z"></path></symbol>
<symbol id="vjs-icon-captions" viewBox="0 0 512 512"><path d="M407.3 76.8H104.6q-18 0-30.5 12.7t-12.5 30.3v259.4q0 18 12.5 30.6t30.5 12.4h302.6q18 0 30.4-12.4t12.6-30.6V119.9q0-17.5-12.6-30.3t-30.4-12.8zm-173 151h-32v-10.6h-43.6v65h43.6v-11h32v21.6q0 9-6.3 15.2t-15.3 6.3h-65q-9 0-15.2-6.3t-6.3-15.3v-86.4q0-9 6.3-15.3t15.3-6.3h65q9 0 15.2 6.3t6.3 15.3v21.4zm151.4 0h-32.4v-10.6h-43v65h43v-11h32.4v21.6q0 9-6.2 15.2t-15.3 6.3h-65q-9 0-15.2-6.3t-6.3-15.3v-86.4q0-9 6.3-15.3t15.3-6.3h65q9 0 15.2 6.3t6.3 15.3v21.4z"></path></symbol>
<symbol id="vjs-icon-settings" viewBox="0 0 512 512"><path d="m402.6 268.5 1-9.5.4-9.5q0-5.2-.5-10l-1-9.4 41.8-32.8q2.9-1.9 3.6-5.4t-1.2-7l-39.4-68.2q-1.9-3.3-5.4-4.5t-7 .2L346.2 132q-7.6-5.7-15.9-10.7t-17.3-8.8l-7.6-52.2q-.4-3.3-3.3-5.7t-6.6-2.3h-78.8q-3.8 0-6.6 2.3t-3.3 5.7l-7.1 52.2q-9 3.8-17.6 8.8t-16.1 10.7L117 112.4q-3.3-1.4-6.9-.2t-5.4 4.5L65.3 185q-1.9 2.8-1.2 6.4t3.6 6l41.7 32.7-1 9.4-.4 10q0 4.7.5 9.5l1 9.5-41.8 32.7q-2.9 2.4-3.6 6t1.2 6.8l39.4 68.3q1.9 3.3 5.4 4.5t7-.2l48.8-20q7.6 5.8 16.1 10.7t17.6 8.8l7 52.2q.5 3.8 3.4 6.2t6.6 2.3h78.8q3.8 0 6.4-2.3t3.5-6.2l7.2-52.2q9-3.8 17.5-8.8t16.1-10.6l48.9 19.9q3.3 1 6.9 0t5.4-4.3l39.4-68.3q1.9-3.3 1.2-6.9t-3.6-5.9l-41.7-32.7zM256 318.3q-28.5 0-48.9-20.2t-20.4-48.6q0-29 20.4-49.1t48.9-20.2 48.9 20.2 20.4 49.1q0 28.5-20.4 48.6T256 318.3z"></path></symbol>
<symbol id="vjs-icon-close" viewBox="0 0 512 512"><path d="M256 34q-45 0-84 17-39 16-69 46t-46 68q-17 40-17 85 0 44 17 84t46 68 69 47q39 17 84 17t84-17q39-17 69-47t46-68 17-84q0-45-17-85-17-39-46-68t-69-46q-39-17-84-17zm108 293-31 31-77-78-77 78-31-31 77-77-77-78 31-30 77 77 77-77 31 30-77 78 77 77z"></path></symbol>
<symbol id="vjs-icon-replay" viewBox="0 0 512 512"><path d="M256.3 119.8V33.3l-108.6 108 108.6 108v-86q26.4 0 50 10 24 10 41.4 27.7t27.6 41.3q10.4 23.4 10.4 50.4t-10.4 50.6q-10 23.4-27.6 41t-41.4 28q-23.6 10-50 10-27 0-50.6-10-24-10.6-41.4-28t-27.6-41q-10.4-23.6-10.4-50.6h-43q0 36 13.5 67.6 13.5 31 37 54.4t55 37q31.4 14 67.4 14 35.6 0 67-14 31.6-13.4 55-37t37-54.4q13.6-31.6 13.6-67.6t-13.6-67.4-37-54.8-55-37.3q-31.4-13.4-67-13.4z"></path></symbol>
<symbol id="vjs-icon-audio" viewBox="0 0 512 512"><path d="M255.8 63q-36.5 0-68.6 14.1-32.2 13.7-56 37.8T93 171.2q-13.7 32.2-13.7 68.6v137.6q0 24.2 17.2 41.4t41.4 17.3h59.1V279h-78.5v-39.3q0-28.4 10.9-53.4t29.3-43.8 43.5-29.6 53.5-10.8q28.8 0 53.9 10.8 24.6 11 43.3 29.6t29.5 43.8 10.9 53.4v39.3h-78.5v157H374q24.1 0 41.4-17.3t17.3-41.4V239.8q0-36.4-13.8-68.6-14.2-32.1-38-56.3t-56-37.8Q292.6 63 255.7 63z"></path></symbol>
<symbol id="vjs-icon-volume1" viewBox="0 0 512 512"><path d="M60.8 188.6v130h86.5l108 108v-346l-108 108z"></path></symbol>
<symbol id="vjs-icon-volume2" viewBox="0 0 512 512"><path d="M61.5 187.8v130H148l108 108v-346l-108 108zm292 65a97.9 97.9 0 0 0-54-87v174a97.9 97.9 0 0 0 54-87z"></path></symbol>
<symbol id="vjs-icon-playlist" viewBox="0 0 512 512"><path d="M47 166h334v56H47v-56zm0-111h334v55H47V55zm0 222h223v56H47v-56zm279 0v167l139-83z"></path></symbol>
<symbol id="vjs-icon-big-play" viewBox="0 0 512 512"><path d="M185.3 98.3v302.4l237.4-151z"></path></symbol>
</defs></svg>`
    const e = new window.DOMParser().parseFromString(svgContent, 'image/svg+xml')
    if (e.querySelector('parsererror')) {
      console.warn('SVG parsing Error')
    }
    else {
      const i = e.documentElement
      i.style.display = 'none'
      this.playerElement.parentNode.insertBefore(i, this.playerElement)
    }
  }

  initPlayerActiveManagement = () => {
    this.toggleControlsVisibility(false)

    const onPointerMove = (e) => {
      if (e.pointerType === 'mouse') {
        this.playerElement.classList.remove(touchInactiveClass)
        this.playerElement.classList.remove(touchActiveClass)
        this.toggleControlsVisibility(false)
      }
    }

    const handleMouseLeave = () => {
      this.playerElement.classList.remove('vjs-has-mouse')
      this.toggleControlsVisibility(true)
    }

    const onTechTouchStart = () => {
      this.player.options_.inactivityTimeout = this.inactivityTouchTimeout
      if (this.playerElement.className.includes(touchActiveClass)) {
        this.markInactive()
      }
      else {
        if (document.pictureInPictureElement)
          return
        this.dom.removeClass(this.playerElement, touchInactiveClass)
        this.dom.addClass(this.playerElement, touchActiveClass)
        this.dom.removeClass(this.playerElement, 'vjs-user-inactive')
        this.dom.addClass(this.playerElement, 'vjs-user-active')
        this.handleControlClick('')
        this.triggerInactiveTimer()
      }
    }

    const onGlobalClick = (e) => {
      this.player.options_.innactivityTimeout = this.inactivityTimeout
      e.stopImmediatePropagation()
      this.handleControlClick(e.target)
    }

    const onGlobalTouchStart = (e) => {
      if (!this.playerElement.contains(e.target)) {
        this.markInactive()
      }
    }

    document.addEventListener('pointermove', onPointerMove)
    window.addEventListener('touchstart', handleMouseLeave, { passive: true })
    this.tech.addEventListener('touchstart', onTechTouchStart, { passive: true })
    this.tech.addEventListener('enterpictureinpicture', this.markInactive)
    window.addEventListener('click', onGlobalClick)
    window.addEventListener('touchstart', onGlobalTouchStart)
    this.player.on('dispose', () => {
      window.removeEventListener('touchstart', handleMouseLeave)
      document.removeEventListener('pointermove', onPointerMove)
      this.tech.removeEventListener('touchstart', onTechTouchStart)
      this.tech.removeEventListener('enterpictureinpicture', this.markInactive)
      window.removeEventListener('click', onGlobalClick)
      window.removeEventListener('touchstart', onGlobalTouchStart)
      this.disposed = true
    })

    let mouseLeaveTimer
    this.player.on('mouseleave', () => {
      clearTimeout(mouseLeaveTimer)
      mouseLeaveTimer = setTimeout(() => {
        this.player.userActive(false)
      }, 100)
    })
  }

  initTextTrackStyle = () => {
    this.player.textTracksStyle = (inputSettings) => {
      if (this.player.textTrackSettings) {
        const textTrackSettings = this.player.textTrackSettings
        const currentSettings = textTrackSettings.getValues()
        const defaultSettings = this.player.captionsSettings

        // Reset to defaults
        textTrackSettings.setDefaults()

        this.player.captionsSettings = { ...defaultSettings, ...currentSettings, ...inputSettings }
        textTrackSettings.setValues(this.player.captionsSettings)
        textTrackSettings.updateDisplay()
      }
    }
    this.player.captionsSettings = { backgroundOpacity: '0', edgeStyle: 'raised' }
    this.player.textTracksStyle(this.player.captionsSettings)
    this.player.on('userinactive', () => {
      this.player.textTrackSettings.updateDisplay()
    })
    this.player.on('useractive', () => {
      this.player.textTrackSettings.updateDisplay()
    })
  }

  initProgressBar = () => {
    const progressHolderEl = vjs_find(this.playerElement, '.vjs-progress-holder')
    const we = vjs_find(this.playerElement, '.vjs-play-progress .vjs-time-tooltip')
    if (we) {
      this.dom.addClass(we, 'vjs-abs-hidden')
    }
    const playProgressEl = vjs_find(progressHolderEl, '.vjs-play-progress')

    const handleProgressDragging = (e) => {
      const i = progressHolderEl.getBoundingClientRect()
      this.dom.addClass(this.playerElement, 'vjs-scrubbing')
      let t = null
      if (e.type === 'touchmove')
        t = getTouchX(e)
      else if (e.type === 'mousemove')
        t = e.pageX
      let s = (t - i.left) / progressHolderEl.offsetWidth
      if (s < 0)
        s = 0
      if (s > 1)
        s = 1
      playProgressEl.style.width = `${(100 * s).toFixed(2)}%`
    }

    const handleProgressDragEnd = (i) => {
      i.preventDefault()
      i.stopPropagation()
      videojs.holderdown = false
      this.dom.removeClass(this.playerElement, 'vjs-scrubbing')
      this.player.userActive(true)
      window.document.removeEventListener('mousemove', handleProgressDragging)
      window.document.removeEventListener('mouseup', handleProgressDragEnd)
      window.document.removeEventListener('touchmove', handleProgressDragging)
      window.document.removeEventListener('touchend', handleProgressDragEnd)
    }

    const handleProgressEvent = (event) => {
      const progressRect = progressHolderEl.getBoundingClientRect()
      let startX = null
      if (event.type === 'touchstart') {
        startX = getTouchX(event)
        window.document.addEventListener('touchmove', handleProgressDragging, false)
        window.document.addEventListener('touchend', handleProgressDragEnd, false)
        this.dom.addClass(this.playerElement, 'vjs-scrubbing')
      }
      else if (event.type === 'mousedown') {
        startX = event.pageX
        window.document.addEventListener('mousemove', handleProgressDragging, false)
        window.document.addEventListener('mouseup', handleProgressDragEnd, false)
      }
      const progress = (startX - progressRect.left) / progressHolderEl.offsetWidth
      playProgressEl.style.width = `${(100 * progress).toFixed(2)}%`
      videojs.holderdown = true
    }

    progressHolderEl.addEventListener('mousedown', handleProgressEvent, false)
    progressHolderEl.addEventListener('touchstart', handleProgressEvent, { passive: true })

    progressHolderEl.addEventListener('touchend', (e) => {
      this.markInactive(e)
    })
  }

  configPlayerBehavior = () => {
    const updatePlayerClassBasedOnWidth = () => {
      const playerWidth = this.player.el_.offsetWidth
      ;[
        'vjs-1600',
        'vjs-1080',
        'vjs-920',
        'vjs-600',
        'vjs-640',
        'vjs-480',
        'vjs-360',
      ].forEach((e) => {
        this.dom.removeClass(this.playerElement, e)
      })
      if (playerWidth < 360) {
        this.dom.addClass(this.playerElement, 'vjs-360')
        this.dom.addClass(this.playerElement, 'vjs-480')
      }
      else if (playerWidth < 480) {
        this.dom.addClass(this.playerElement, 'vjs-480')
      }
      else if (playerWidth < 640) {
        this.dom.addClass(this.playerElement, 'vjs-640')
        if (playerWidth < 600) {
          this.dom.addClass(this.playerElement, 'vjs-600')
        }
      }
      else if (playerWidth < 920) {
        this.dom.addClass(this.playerElement, 'vjs-920')
      }
      else if (playerWidth < 1080) {
        this.dom.addClass(this.playerElement, 'vjs-920')
        this.dom.addClass(this.playerElement, 'vjs-1080')
      }
      else {
        this.dom.addClass(this.playerElement, 'vjs-920')
        this.dom.addClass(this.playerElement, 'vjs-1080')
        this.dom.addClass(this.playerElement, 'vjs-1600')
      }
    }

    updatePlayerClassBasedOnWidth()

    if (matchMedia('(pointer:fine)').matches) {
      this.toggleControlsVisibility(true)
    }

    this.player.on('playerresize', () => {
      updatePlayerClassBasedOnWidth()
      this.handleControlClick('')
    })

    const loadingSpinnerEl = vjs_find(this.playerElement, '.vjs-loading-spinner')

    this.player.on('playing', () => {
      this.dom.removeClass(loadingSpinnerEl, this.hiddenClass)
      this.dom.removeClass(loadingSpinnerEl, 'vjs-abs-hidden')
      if (
        (this.player.remoteTextTracks ? this.player.remoteTextTracks() : []).length < 1
        && videojs.browser.IS_IOS
      ) {
        this.dom.addClass(vjs_find(this.playerElement, '.vjs-subs-caps-button'), this.hiddenClass)
      }
    })

    this.player.on('play', () => {
      loadingSpinnerEl.style.opacity = 1
      if (this.firstPlay) {
        updatePlayerClassBasedOnWidth()
        this.firstPlay = false
      }
    })

    this.player.on('loadeddata', () => {
      if (this.player.isAudio() && !this.dom.hasClass(this.playerElement, 'vjs-audio')) {
        this.dom.addClass(this.playerElement, 'vjs-audio')
      }
    })

    this.player.on('durationchange', () => {
      if (this.player.duration() === 1 / 0 || this.player.duration() >= 4294967296) {
        if (this.player.seekable().length > 0) {
          const i = this.player.seekable().end(0) - this.player.seekable().start(0)
          if (this.dvrMin !== 'none' && i > this.dvrMin) {
            this.player.el_.classList.add('vjs-liveui')
          }
        }
      }
    })
  }

  replaceSvgIcons = () => {
    const updateVolumeControls = (isReady) => {
      const volume = this.player.volume()
      const muteControlEl = vjs_find(this.controlBarEl, '.vjs-mute-control')

      if (!muteControlEl) {
        return
      }

      let n = 'mute'
      if (this.player.muted()) {
        n = 'mute'
      }
      else {
        if (volume > 0) {
          n = 'volume1'
        }
        if (volume > 0.25) {
          n = 'volume2'
        }
        if (volume > 0.75) {
          n = 'volume3'
        }
      }
      if (isReady) {
        muteControlEl.querySelector('.vjs-icon-placeholder').setAttribute('data-id', n)
      }
      else {
        setIcon(muteControlEl, n)
      }
    }

    const buttonMap = {
      'vjs-big-play-button': 'big-play',
      'vjs-picture-in-picture-control': 'pip',
      'vjs-fullscreen-control': 'fullscreen',
      'vjs-play-control': 'play',
      'vjs-subs-caps-button': 'captions',
      'vjs-audio-button': 'audio',
    }

    Object.keys(buttonMap).forEach((className) => {
      const element = vjs_find(this.playerElement, `.${className}`)
      if (element) {
        const iconPlaceholder = element.querySelector('.vjs-icon-placeholder')
        if (iconPlaceholder) {
          iconPlaceholder.setAttribute('data-id', buttonMap[className])
        }
      }
    })

    updateVolumeControls(true)
    this.playerElement.querySelectorAll('.vjs-icon-placeholder').forEach((e) => {
      if (e.getAttribute('data-id')) {
        const i = e.getAttribute('data-id').trim()
        const t = 'http://www.w3.org/2000/svg'
        const s = document.createElementNS(t, 'svg')
        s.setAttributeNS(null, 'viewBox', '0 0 32 32')
        const n = document.createElementNS(t, 'use')
        s.appendChild(n)
        n.setAttributeNS(null, 'href', `#vjs-icon-${i}`)
        e.appendChild(s)
        videojs.dom.addClass(e, 'vjs-svg-icon')
      }
    })
    this.player.on('pause', () => {
      setIcon(vjs_find(this.controlBarEl, '.vjs-play-control'), 'play')
      setIcon(vjs_find(this.playerElement, '.vjs-b-p-b'), 'play')
    })
    this.player.on('play', () => {
      setIcon(vjs_find(this.controlBarEl, '.vjs-play-control'), 'pause')
      setIcon(vjs_find(this.playerElement, '.vjs-b-p-b'), 'pause')
    })
    this.player.on('ended', () => {
      setIcon(vjs_find(this.controlBarEl, '.vjs-play-control'), 'replay')
    })
    this.tech.addEventListener('enterpictureinpicture', () => {
      setIcon(vjs_find(this.controlBarEl, '.vjs-picture-in-picture-control'), 'pip-full')
    })
    this.tech.addEventListener('leavepictureinpicture', () => {
      setIcon(vjs_find(this.controlBarEl, '.vjs-picture-in-picture-control'), 'pip')
    })
    this.player.on('volumechange', () => {
      updateVolumeControls()
    })
    this.player.on('fullscreenchange', () => {
      const t = vjs_find(this.controlBarEl, '.vjs-fullscreen-control')
      if (this.player.isFullscreen()) {
        setIcon(t, 'fullscreen-back')
      }
      else {
        setIcon(t, 'fullscreen')
      }
    })
  }

  markInactive = () => {
    clearTimeout(this.touchtimer)
    this.dom.removeClass(this.playerElement, touchActiveClass)
    this.dom.addClass(this.playerElement, touchInactiveClass)
    this.dom.removeClass(this.playerElement, 'vjs-user-active')
    this.dom.addClass(this.playerElement, 'vjs-user-inactive')
  }

  toggleControlsVisibility = (isHidden) => {
    if (isHidden) {
      this.playerElement.classList.remove('vjs-has-mouse')
    }
    else {
      this.playerElement.classList.add('vjs-has-mouse')
    }
  }

  triggerInactiveTimer = () => {
    clearTimeout(this.touchtimer)
    this.player.userActive(false)
    this.touchtimer = setTimeout(this.markInactive, this.inactivityTouchTimeout)
  }

  handleControlClick = (controlEl) => {
    if (!this.player.isDisposed() && !this.disposed) {
      if (!controlEl) {
        controlEl = vjs_find(this.playerElement, '.vjs-play-control')
      }

      this.player.trigger('controlClick', controlEl)
    }
  }
}

export default DubbUiPlugin
